import * as React from "react"
import { Link, useForm, usePage } from "@inertiajs/react"
import { PublicHero, PublicShell } from "@/components/PublicShell"
import { BrochureButton, useBrochureAvailable } from "@/components/public/BrochureGate"
import { PublicMeta } from "@/components/public/PublicMeta"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

const TITLE = "Request a Consultation"
const DESCRIPTION =
  "Tell us about your occasion and we will guide you through the wines, your own private label design and delivery anywhere in the UK."

// Values match Enquiry::EVENT_TYPES (underscored), labels match the occasion pages.
const EVENT_TYPES = [
  { value: "corporate", label: "Corporate" },
  { value: "weddings", label: "Weddings" },
  { value: "hospitality", label: "Hospitality" },
  { value: "private_events", label: "Private Events" },
  { value: "sporting_events", label: "Sporting Events" },
  { value: "other", label: "Something else" },
]

// Private Cellar field treatment lives in the design system
// (design-system.css: .field-label, .form-control-cellar, .form-file-cellar).
const FIELD = "form-control-cellar"

type ContactLink = { label: string; href: string } | null

type ContactProps = {
  contact_details: {
    phone: ContactLink
    email: ContactLink
    whatsapp: ContactLink
  }
  prefill: { name: string; email: string }
}

type FormData = {
  name: string
  company: string
  phone: string
  email: string
  event_type: string
  bottle_count: string
  event_date: string
  message: string
  artwork: File | null
  website: string
}

export default function Contact() {
  const { props } = usePage<PageProps<ContactProps>>()
  const { contact_details: details, prefill } = props

  const form = useForm<FormData>({
    name: prefill.name,
    company: "",
    phone: "",
    email: prefill.email,
    event_type: "",
    bottle_count: "",
    event_date: "",
    message: "",
    artwork: null,
    website: "",
  })

  const errors = form.errors as Partial<Record<keyof FormData | "base", string>>
  const baseError = props.errors?.base

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.post("/contact", { forceFormData: true, preserveScroll: false })
  }

  const text =
    (field: Exclude<keyof FormData, "artwork">) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      form.setData(field, event.target.value)

  const hasErrors = Object.keys(errors).length > 0

  return (
    <PublicShell>
      <PublicMeta title={TITLE} description={DESCRIPTION} />

      <PublicHero
        eyebrow="Get in touch"
        title={
          <>
            Request a <span className="soft">consultation</span>
          </>
        }
        lede="Tell us about your occasion and we will shape the wines, the label and the delivery around it."
      />

      <section className="band band-charcoal pt-0" aria-label="Consultation form">
        <div className="shell">
          <div className="grid gap-16 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:gap-20">
            <form onSubmit={submit} className="min-w-0" aria-describedby="form-intro">
              <p id="form-intro" className="mb-10 max-w-[52ch]">
                A few details help us come back with the right wines and a label idea. Fields marked
                with an asterisk are required; everything else can wait for our conversation.
              </p>

              {(hasErrors || baseError) && (
                <div role="alert" className="mb-10 border border-danger/60 bg-danger-faded px-5 py-4">
                  <span className="text-danger-display">
                    {baseError ?? "Please check the highlighted fields and try again."}
                  </span>
                </div>
              )}

              <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
                <Field id="name" label="Name *" error={errors.name}>
                  <Input
                    id="name"
                    autoComplete="name"
                    required
                    value={form.data.name}
                    onChange={text("name")}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? "name-error" : undefined}
                    className={FIELD}
                  />
                </Field>

                <Field id="company" label="Company" hint="Optional" error={errors.company}>
                  <Input
                    id="company"
                    autoComplete="organization"
                    value={form.data.company}
                    onChange={text("company")}
                    className={FIELD}
                  />
                </Field>

                <Field id="email" label="Email *" error={errors.email}>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={form.data.email}
                    onChange={text("email")}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    className={FIELD}
                  />
                </Field>

                <Field id="phone" label="Phone" error={errors.phone}>
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.data.phone}
                    onChange={text("phone")}
                    className={FIELD}
                  />
                </Field>

                <Field id="event_type" label="Occasion *" error={errors.event_type}>
                  <Select
                    id="event_type"
                    required
                    value={form.data.event_type}
                    onChange={text("event_type")}
                    aria-invalid={Boolean(errors.event_type)}
                    aria-describedby={errors.event_type ? "event_type-error" : undefined}
                    className={FIELD}
                  >
                    <option value="" disabled>
                      Choose an occasion
                    </option>
                    {EVENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field
                  id="bottle_count"
                  label="Approximate bottles"
                  hint="Our minimum is 100, mixed as you like"
                  error={errors.bottle_count}
                >
                  <Input
                    id="bottle_count"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    placeholder="e.g. 120"
                    value={form.data.bottle_count}
                    onChange={text("bottle_count")}
                    aria-invalid={Boolean(errors.bottle_count)}
                    aria-describedby={errors.bottle_count ? "bottle_count-error" : "bottle_count-hint"}
                    className={FIELD}
                  />
                </Field>

                <Field id="event_date" label="Date required" error={errors.event_date}>
                  <Input
                    id="event_date"
                    type="date"
                    value={form.data.event_date}
                    onChange={text("event_date")}
                    className={FIELD}
                  />
                </Field>

                <Field
                  id="artwork"
                  label="Your logo or artwork"
                  hint="Optional. Any image or PDF up to 25 MB"
                  error={errors.artwork}
                >
                  <input
                    id="artwork"
                    type="file"
                    accept="image/*,.pdf,.ai,.eps,.svg"
                    onChange={(event) => form.setData("artwork", event.target.files?.[0] ?? null)}
                    aria-invalid={Boolean(errors.artwork)}
                    aria-describedby={errors.artwork ? "artwork-error" : "artwork-hint"}
                    className="form-file-cellar"
                  />
                </Field>

                <Field id="message" label="Tell us about it" className="sm:col-span-2" error={errors.message}>
                  <Textarea
                    id="message"
                    rows={6}
                    placeholder="The occasion, the guests, the style of wine you have in mind, any label ideas."
                    value={form.data.message}
                    onChange={text("message")}
                    className={FIELD}
                  />
                </Field>

                {/* Honeypot: hidden from people and assistive tech; bots fill it. */}
                <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.data.website}
                    onChange={text("website")}
                  />
                </div>
              </div>

              <div className="mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center">
                <Button type="submit" variant="gold" size="lg" disabled={form.processing}>
                  {form.processing ? "Sending…" : "Send my enquiry"}
                </Button>
                <div className="text-[13px] text-ink-muted">
                  We use your details only to reply. See our <Link href="/privacy">privacy policy</Link>.
                </div>
              </div>
            </form>

            <DirectDetails details={details} />
          </div>
        </div>
      </section>
    </PublicShell>
  )
}

function Field({
  id,
  label,
  hint,
  error,
  className,
  children,
}: {
  id: string
  label: string
  hint?: string
  error?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      {children}
      {error ? (
        <div id={`${id}-error`} className="field-error">
          {error}
        </div>
      ) : (
        hint && (
          <div id={`${id}-hint`} className="field-hint">
            {hint}
          </div>
        )
      )}
    </div>
  )
}

function DirectDetails({ details }: { details: ContactProps["contact_details"] }) {
  const rows: { term: string; link: ContactLink; placeholder: string; external?: boolean }[] = [
    { term: "Telephone", link: details.phone, placeholder: "Phone number to be confirmed" },
    { term: "Email", link: details.email, placeholder: "Email address to be confirmed" },
    { term: "WhatsApp", link: details.whatsapp, placeholder: "WhatsApp number to be confirmed", external: true },
  ]

  return (
    <aside aria-labelledby="direct-details-title" className="min-w-0 self-start border border-hairline bg-page/40 p-8 sm:p-10">
      <div className="eyebrow">Speak to us directly</div>
      <h2 id="direct-details-title">Prefer a conversation?</h2>
      <p className="mt-4">
        Call, email or message Duncan and he will talk you through the wines, the label and the timings.
      </p>

      <dl className="mt-8 border-t border-hairline-soft">
        {rows.map((row) => (
          <div key={row.term} className="border-b border-hairline-soft py-5">
            <dt className="footer-heading mb-1.5">{row.term}</dt>
            <dd>
              {row.link ? (
                <a
                  href={row.link.href}
                  {...(row.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                  {row.link.label}
                </a>
              ) : (
                <span className="text-ink-muted">{row.placeholder}</span>
              )}
            </dd>
          </div>
        ))}
        <div className="border-b border-hairline-soft py-5">
          <dt className="footer-heading mb-1.5">Delivery</dt>
          <dd className="text-ink-display">Delivering throughout the UK</dd>
        </div>
      </dl>

      <AsideBrochure />
    </aside>
  )
}

/** The brochure in the aside (M7 decision 12); nothing without a brochure. */
function AsideBrochure() {
  if (!useBrochureAvailable()) return null

  return (
    <div className="brochure-cta brochure-cta-stacked">
      <p>Prefer to read first? The wines, the five steps and bottles we have made, in one PDF.</p>
      <BrochureButton className="w-full" />
    </div>
  )
}
